import { useBrowseProductEntitlementApi } from '@aibees/shared/api';
import type {
  CatalogFeature,
  CatalogFeatureChild,
  CatalogProduct,
  Plan,
} from '@aibees/shared/definition';
import usePriceAndPlanStore from '@aibees/shared/features/price-and-plan/store';
import { useMemo } from 'react';
import { parseValueForSlug } from '../pricing-cards/catalog';
import { usePlanList } from '../pricing-cards/hooks/use-plan-list';
import type {
  CellValue,
  FeatureRow,
  FeatureSection,
  SectionCoverage,
} from './types';

function sortProducts(products: CatalogProduct[]): CatalogProduct[] {
  return [...products].sort((a, b) => {
    const orderA = Number(a.metadata?.order) || 0;
    const orderB = Number(b.metadata?.order) || 0;
    return orderA - orderB;
  });
}

function resolveCellValue(
  feature: CatalogFeature,
  productId: string,
  slug: string
): CellValue {
  if (!feature.products.includes(productId)) return 'dash';
  const cell = feature.cells[productId];
  if (!cell) return 'dash';

  if (cell.coming_soon === 'true') return 'comingSoon';

  const value = parseValueForSlug(cell.values_per_plan, slug);
  if (value) return value;

  return 'check';
}

function resolveChildValue(
  child: CatalogFeatureChild,
  productId: string
): CellValue {
  if (!child.products.includes(productId)) return 'dash';
  if (child.metadata.coming_soon === 'true') return 'comingSoon';
  return 'check';
}

function buildRow(
  feature: CatalogFeature,
  products: CatalogProduct[]
): FeatureRow {
  const values: Record<string, CellValue> = {};
  for (const product of products) {
    const slug = product.metadata.slug ?? '';
    values[product.id] = resolveCellValue(feature, product.id, slug);
  }
  return {
    id: feature.metadata.code || feature.key,
    feature: feature.name,
    values,
  };
}

function buildSection(
  feature: CatalogFeature,
  products: CatalogProduct[]
): FeatureSection {
  const children = feature.children ?? [];
  const headerValues: Record<string, CellValue> = {};
  const headerCoverage: Record<string, SectionCoverage> = {};

  for (const product of products) {
    const filled = children.filter((c) =>
      c.products.includes(product.id)
    ).length;
    headerCoverage[product.id] = { filled, total: children.length };
    headerValues[product.id] = filled > 0 ? 'check' : 'dash';
  }

  const featureRows: FeatureRow[] = children.map((child) => {
    const values: Record<string, CellValue> = {};
    for (const product of products) {
      values[product.id] = resolveChildValue(child, product.id);
    }
    return {
      id: child.metadata.code || child.key,
      feature: child.name,
      values,
    };
  });

  return {
    id: feature.metadata.code || feature.key,
    title: feature.name,
    headerValues,
    headerCoverage,
    features: featureRows,
  };
}

function sortBySectionOrder(features: CatalogFeature[]): CatalogFeature[] {
  return [...features].sort((a, b) => {
    const aOrder = Number(a.metadata.section_order ?? 999);
    const bOrder = Number(b.metadata.section_order ?? 999);
    return aOrder - bOrder;
  });
}

function groupPlansByProductId(
  productCards: { product: { id: string }; plans: Plan[] }[]
): Record<string, Plan[]> {
  const grouped: Record<string, Plan[]> = {};
  for (const entry of productCards) {
    grouped[entry.product.id] = entry.plans;
  }
  return grouped;
}

export function useCollapsedTableData() {
  const yearly = usePriceAndPlanStore.useYearly();
  const { data, isLoading: catalogLoading } = useBrowseProductEntitlementApi();
  const { productCards, isLoading: plansLoading } = usePlanList(yearly);

  const isLoading = catalogLoading || plansLoading;

  return useMemo(() => {
    const catalogProducts: CatalogProduct[] = data?.products ?? [];
    const catalogFeatures: CatalogFeature[] = data?.features ?? [];

    const sortedProducts = sortProducts(catalogProducts);
    const products: CatalogProduct[] = sortedProducts;
    const sortedFeatures = sortBySectionOrder(catalogFeatures);

    const flatFeatures = sortedFeatures.filter(
      (f) => !(f.children?.length ?? 0)
    );
    const sectionedFeatures = sortedFeatures.filter(
      (f) => (f.children?.length ?? 0) > 0
    );

    const topRows: FeatureRow[] = flatFeatures
      .filter((f) => Boolean(f.metadata.section))
      .map((f) => buildRow(f, sortedProducts));

    const sections: FeatureSection[] = sectionedFeatures.map((f) =>
      buildSection(f, sortedProducts)
    );

    return {
      products,
      topRows,
      sections,
      isLoading,
      plansByProduct: groupPlansByProductId(productCards),
      yearly,
    };
  }, [data, isLoading, productCards, yearly]);
}
